import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const SavesCtx = createContext(null);

export function SavesProvider({ children }) {
    const { user } = useAuth();
    const [photoIds, setPhotoIds] = useState(new Set());
    const [productIds, setProductIds] = useState(new Set());

    const refresh = useCallback(async () => {
        if (!user) { setPhotoIds(new Set()); setProductIds(new Set()); return; }
        try {
            const { data } = await api.get("/my/saves/ids");
            setPhotoIds(new Set(data.photo));
            setProductIds(new Set(data.product));
        } catch { /* ignore */ }
    }, [user]);

    useEffect(() => { refresh(); }, [refresh]);

    const isSaved = (kind, id) => (kind === "photo" ? photoIds : productIds).has(id);

    const toggle = async (kind, id) => {
        if (!user) return { need_auth: true };
        const setter = kind === "photo" ? setPhotoIds : setProductIds;
        const has = (kind === "photo" ? photoIds : productIds).has(id);
        // optimistic
        setter((prev) => {
            const next = new Set(prev);
            has ? next.delete(id) : next.add(id);
            return next;
        });
        try {
            if (has) {
                await api.delete(`/saves/${kind}/${id}`);
            } else {
                await api.post("/saves", { target_type: kind, target_id: id });
            }
            return { saved: !has };
        } catch (e) {
            // rollback
            setter((prev) => {
                const next = new Set(prev);
                has ? next.add(id) : next.delete(id);
                return next;
            });
            return { error: true };
        }
    };

    const count = photoIds.size + productIds.size;

    return (
        <SavesCtx.Provider value={{ isSaved, toggle, refresh, count, photoIds, productIds }}>
            {children}
        </SavesCtx.Provider>
    );
}

export const useSaves = () => useContext(SavesCtx);
